import {
  useQuery,
  useQueryClient,
  useMutation,
  useQueries,
  UseQueryResult,
} from '@tanstack/react-query'
import {
  CustomRules,
  CustomSchedule,
  MapStringPrioritySchedulingEventPriorityScheduling,
  MapStringPrioritySchedulingSeriesPriorityScheduling,
  ScheduleDraftView,
  SchedulePublishedViewSchedule,
  DefaultSchedule1,
  Schedule,
} from '@chilipiper/api-client-user-availability'
import { useFireSessionContext } from '@chilipiper/fire-session'
import keyBy from 'lodash/keyBy'
import uniq from 'lodash/uniq'
import { omit, sortBy } from 'lodash'
import { startOfWeek } from '@chilipiper/date-time'
import { useCallback } from 'react'
import { DeepPartial } from '../types/helpers'
import { useCalendarIntegrationStatus } from '../components/personal-settings/integrations/apiQueries/integrations'
import { useApiClient } from './apiClientContext'
import { userAvailabilityQueryKeyFactory as queryKeyFactory } from '@/api/api-client/schedulingQueryKeysShim'

export const useUserSyncedTimezone = () => {
  const { tenantId, userId } = useFireSessionContext()
  const { userAvailability } = useApiClient()

  return useQuery({
    queryKey: ['user-synced-timezone'],
    queryFn: () =>
      userAvailability.getUserSyncedTimezone(tenantId, userId).catch(e => {
        const isTimezoneNotDefined =
          e instanceof Response &&
          'error' in e &&
          typeof e.error === 'object' &&
          e.error &&
          'errorCode' in e.error &&
          e.error.errorCode === 'user-availability-service.timezone-not-defined'
        if (e.status === 404 || isTimezoneNotDefined) {
          return null
        }

        throw e
      }),
    refetchOnWindowFocus: false,
  })
}

export const useCalendarSyncStatus = () => {
  const { tenantId, userId } = useFireSessionContext()
  const { data: syncedTimezone } = useUserSyncedTimezone()
  const { data: integrationStatus } = useCalendarIntegrationStatus(userId, tenantId)

  const isCalendarConnected =
    integrationStatus?.primary?.type === 'Success' &&
    integrationStatus.primary.value.type === 'ConnectedAccount'

  return { syncedTimezone, isCalendarConnected }
}

export interface TypedScheduleDraftView<
  Type extends
    SchedulePublishedViewSchedule['schedule'] = SchedulePublishedViewSchedule['schedule'],
> extends ScheduleDraftView {
  draft: DeepPartial<Type>
}

export interface TypedSchedulePublishedView<
  Type extends
    SchedulePublishedViewSchedule['schedule'] = SchedulePublishedViewSchedule['schedule'],
> extends SchedulePublishedViewSchedule {
  schedule: Type
}

export type AvailabilitySchedule =
  | {
      draft?: TypedScheduleDraftView<DefaultSchedule1>
      id: string
      isDefault: true
      published?: TypedSchedulePublishedView<DefaultSchedule1>
    }
  | {
      draft: TypedScheduleDraftView<CustomSchedule>
      id: string
      isDefault: false
      published?: TypedSchedulePublishedView<CustomSchedule>
    }

export const useAvailabilitySchedules = (compare = false) => {
  const { tenantId, userId } = useFireSessionContext()
  const { userAvailability } = useApiClient()

  return useQueries({
    queries: [
      {
        queryKey: ['user-availability-schedules-draft', tenantId, userId, compare],
        queryFn: () => {
          return userAvailability
            .getApiV1TenantTenantidUserUseridAvailabilitySchedulesDrafts(tenantId, userId, {
              compare,
            })
            .catch(e => {
              if (e.status === 404) {
                return []
              }

              throw e
            }) as Promise<TypedScheduleDraftView[]>
        },
        refetchOnWindowFocus: false,
        enabled: Boolean(tenantId && userId),
      },
      {
        queryKey: ['user-availability-schedules-published', tenantId, userId],
        queryFn: () => {
          return userAvailability
            .getApiV1TenantTenantidUserUseridAvailabilitySchedules(tenantId, userId)
            .catch(e => {
              if (e.status === 404) {
                return []
              }

              throw e
            })
        },
        refetchOnWindowFocus: false,
        enabled: Boolean(tenantId && userId),
      },
    ],
    combine: useCallback(
      (
        results: Array<
          UseQueryResult<
            TypedScheduleDraftView<Schedule>[] | SchedulePublishedViewSchedule[],
            Error
          >
        >
      ) => {
        const isLoading = results.some(result => result.isLoading)
        const isError = results.some(result => result.isError)
        const resultData = results.every(result => result.data)
          ? results.map(result => result.data)
          : []
        const draftsData = (resultData[0] ?? []) as TypedScheduleDraftView<Schedule>[]
        const publishedData = resultData[1] ?? ([] as SchedulePublishedViewSchedule[])

        // default availability is created as published and has no draft. we can have a draft without published
        // so we need to merge these ids to get the full list of ids and iterate
        const ids = uniq([
          ...draftsData.map(draft => draft.id),
          ...publishedData.map(published => published.id),
        ])

        const publishedById = keyBy(publishedData, 'id')
        const draftById = keyBy(draftsData, 'id')
        const data = ids.map(id => {
          const draft = draftById[id]
          const published = publishedById[id] as SchedulePublishedViewSchedule

          return {
            id,
            draft: draft ?? {
              draft: {
                id,
                metadata: published.metadata,
                ...published.schedule,
              },
            },
            published,
            isDefault: published?.schedule.type === 'DefaultSchedule',
          }
        }) as AvailabilitySchedule[]

        const sortedData = sortBy(data, item => {
          if (item.isDefault) {
            return 0 // isDefault should be first
          } else {
            return Date.parse(
              (item.draft?.metadata?.updatedAt || item.published?.metadata.updatedAt) ?? ''
            ) // sort by the date property
          }
        })
        return { data: sortedData, isLoading, isError }
      },
      []
    ),
  })
}

export const useCreateAvailabilityScheduleDraft = () => {
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()

  return useMutation({
    mutationFn: (data: TypedScheduleDraftView['draft']) => {
      if (data.type === 'CustomSchedule') {
        return userAvailability.postApiV1TenantTenantidUserUseridAvailabilitySchedulesDrafts(
          tenantId,
          userId,
          data
        )
      }
      return userAvailability.postApiV1TenantTenantidUserUseridAvailabilitySchedulesDrafts(
        tenantId,
        userId,
        {
          name: data.name,
          type: 'CustomSchedule',
          activeOn: { type: 'Always' },
          assignedTo: { type: 'NoAssignment' },
          isStrictlyReserved: false,
          tz: data.tz,
          workingHours: data.workingHours,
        }
      )
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-draft'] }),
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-published'] }),
      ])
    },
  })
}

export const useUpdateAvailabilityScheduleDraft = (availabilityId: string) => {
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()

  return useMutation({
    mutationFn: (data: TypedScheduleDraftView['draft']) => {
      return userAvailability.putApiV1TenantTenantidUserUseridAvailabilitySchedulesDraftsScheduleid(
        tenantId,
        userId,
        availabilityId,
        data
      )
    },
    onMutate: async updates => {
      await queryClient.cancelQueries({ queryKey: ['user-availability-schedules-draft'] })
      const previousSchedules = queryClient.getQueryData<ScheduleDraftView[]>([
        'user-availability-schedules-draft',
      ])

      const updatedSchedules = previousSchedules?.map(schedule => {
        if (schedule.id === availabilityId) {
          return { ...schedule, draft: { ...schedule.draft, ...updates } }
        }
        return schedule
      })
      queryClient.setQueryData(['user-availability-schedules-draft'], updatedSchedules)
      return { previousSchedules, updatedSchedules }
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-draft'] })
    },
    onError: (error, updates, context) => {
      queryClient.setQueryData(['user-availability-schedules-draft'], context?.previousSchedules)
      return queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-draft'] })
    },
  })
}

export const usePublishAvailabilityScheduleDraft = (availabilityId: string) => {
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()

  return useMutation({
    mutationFn: (draft: TypedScheduleDraftView['draft']) => {
      return userAvailability.putApiV1TenantTenantidUserUseridAvailabilitySchedulesScheduleid(
        tenantId,
        userId,
        availabilityId,
        draft as CustomSchedule
      )
    },
    onMutate: async updates => {
      await queryClient.cancelQueries({ queryKey: ['user-availability-schedules-published'] })
      const previousSchedules = queryClient.getQueryData<ScheduleDraftView[]>([
        'user-availability-schedules-published',
      ])
      const updatedSchedules = previousSchedules?.map(schedule => {
        if (schedule.id === availabilityId) {
          return { ...schedule, schedule: { ...updates } }
        }
        return schedule
      })
      queryClient.setQueryData(['user-availability-schedules-published'], updatedSchedules)
      return { previousSchedules, updatedSchedules }
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-published'] }),
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-draft'] }), // draftState changes after publish
      ])
    },
    onError: (error, updates, context) => {
      queryClient.setQueryData(
        ['user-availability-schedules-published'],
        context?.previousSchedules
      )
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-published'] }),
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-draft'] }), // draftState changes after publish
      ])
    },
  })
}

export type AvailabilityScheduleDraftWrite = {
  draft: TypedScheduleDraftView<CustomSchedule>['draft']
  scheduleId: string
}

/**
 * Saves and publishes updated drafts for several schedules in one mutation. Moving a meeting type
 * between availability schedules touches up to two schedules (added to one, removed from the
 * other), which must share a pending state and invalidation.
 */
export const useSaveAndPublishAvailabilityScheduleDrafts = () => {
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()

  return useMutation({
    mutationFn: (writes: AvailabilityScheduleDraftWrite[]) =>
      Promise.all(
        writes.map(async ({ scheduleId, draft }) => {
          const saved =
            await userAvailability.putApiV1TenantTenantidUserUseridAvailabilitySchedulesDraftsScheduleid(
              tenantId,
              userId,
              scheduleId,
              draft
            )
          return userAvailability.putApiV1TenantTenantidUserUseridAvailabilitySchedulesScheduleid(
            tenantId,
            userId,
            scheduleId,
            saved.draft as CustomSchedule
          )
        })
      ),
    onSettled: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-draft'] }),
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-published'] }),
      ])
    },
  })
}

export const usePublishAvailabilityScheduleDraftTimezone = (availabilityId: string) => {
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()

  return useMutation({
    mutationFn: (draft: TypedScheduleDraftView['draft']) => {
      return userAvailability.putApiV1TenantTenantidUserUseridAvailabilitySchedulesScheduleid(
        tenantId,
        userId,
        availabilityId,
        draft as CustomSchedule
      )
    },
    onMutate: async (updates: TypedScheduleDraftView['draft']) => {
      const previousPublishedAvailabilitySchedules = queryClient.getQueryData<
        TypedSchedulePublishedView[]
      >(['user-availability-schedules-published'])

      const updatedPublishedAvailabilitySchedules = (
        previousPublishedAvailabilitySchedules as TypedSchedulePublishedView[]
      ).map(previousPublishedAvailabilitySchedule => {
        if (previousPublishedAvailabilitySchedule.id === availabilityId) {
          return {
            ...previousPublishedAvailabilitySchedule,
            schedule: {
              ...previousPublishedAvailabilitySchedule.schedule,
              tz: updates.tz,
            },
          }
        }
        return previousPublishedAvailabilitySchedule
      })

      queryClient.setQueryData(
        ['user-availability-schedules-published'],
        updatedPublishedAvailabilitySchedules
      )

      return { previousPublishedAvailabilitySchedules, updatedPublishedAvailabilitySchedules }
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-published'] }),
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-draft'] }),
      ])
    },
    onError: (error, _updates, context) => {
      console.error(error)
      queryClient.setQueryData(
        ['user-availability-schedules-published'],
        context?.previousPublishedAvailabilitySchedules
      )
    },
  })
}

export const useDeleteAvailabilitySchedule = () => {
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()

  return useMutation({
    mutationFn: (data: AvailabilitySchedule) => {
      return Promise.all([
        userAvailability.deleteApiV1TenantTenantidUserUseridAvailabilitySchedulesDraftsScheduleid(
          tenantId,
          userId,
          data.id
        ),
        userAvailability
          .deleteApiV1TenantTenantidUserUseridAvailabilitySchedulesScheduleid(
            tenantId,
            userId,
            data.id
          )
          .catch(e => {
            if (e.status === 404) {
              return
            }

            throw e
          }),
      ])
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-draft'] }),
        queryClient.invalidateQueries({ queryKey: ['user-availability-schedules-published'] }),
      ])
    },
  })
}

export const usePrioritySettingsForMultipleUsers = (
  eventsIds: string[],
  isEnabled: boolean = true
) => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  return useQuery({
    queryKey: ['priority-settings-multiple-users', tenantId, eventsIds, userId],
    queryFn: () => {
      return userAvailability
        .putApiV1TenantTenantidPrioritySchedulingConfigBatch(tenantId, {
          users: [userId],
          events: eventsIds,
        })
        .then(data => data.result)
    },
    refetchOnWindowFocus: false,
    enabled: eventsIds.length > 0 && isEnabled,
  })
}

export const usePrioritySeriesSettingsMultipleUsers = (seriesEventIds: string[]) => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  return useQuery({
    queryKey: ['priority-settings-series-multiple-users', seriesEventIds, userId],
    queryFn: () => {
      return userAvailability
        .putApiV1TenantTenantidPrioritySchedulingSeriesConfigBatch(tenantId, {
          users: [userId],
          events: seriesEventIds,
        })
        .then(data => data.result)
    },
    refetchOnWindowFocus: false,
    enabled: seriesEventIds.length > 0,
  })
}

export const useDeleteSeriesPrioritySettings = (seriesIds: string[]) => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (seriesId: string) =>
      userAvailability.deletePrioritySchedulingConfigurationForSeriesOfEvents(
        tenantId,
        userId,
        seriesId
      ),
    onMutate: seriesId => {
      const previousSeriesSettings =
        queryClient.getQueryData<MapStringPrioritySchedulingSeriesPriorityScheduling>(
          queryKeyFactory.seriesSettings(seriesIds)
        )
      const updatedSeriesSettings = omit(previousSeriesSettings, seriesId)
      queryClient.setQueryData(queryKeyFactory.seriesSettings(seriesIds), updatedSeriesSettings)

      return { previousSeriesSettings, updatedSeriesSettings }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeyFactory.seriesSettings(seriesIds),
      })
    },
    onError: (_err, _updates, context) => {
      queryClient.setQueryData(
        queryKeyFactory.seriesSettings(seriesIds),
        context?.previousSeriesSettings
      )
    },
  })
}

export const useDeleteCustomRulesPrioritySettings = () => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      userAvailability.deleteCustomPrioritySchedulingConfiguration(tenantId, userId),
    onMutate: () => {
      const previousCustomRules = queryClient.getQueryData<CustomRules>(['custom-rules'])
      const updatedCustomRules: CustomRules = {
        noAttendees: undefined,
      }
      queryClient.setQueryData(['custom-rules'], updatedCustomRules)

      return { previousCustomRules, updatedCustomRules }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custom-rules'] })
    },
    onError: (_err, _updates, context) => {
      queryClient.setQueryData(['custom-rules'], context?.previousCustomRules)
    },
  })
}

export const useDeleteEventPrioritySettings = (
  eventsIds: string[],
  calendarRangeDifference: string,
  startTime = startOfWeek(new Date()).toISOString()
) => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (eventId: string) =>
      userAvailability.deletePrioritySchedulingConfiguration(tenantId, userId, eventId),
    onMutate: (eventId: string) => {
      const previousEventsPrioritySettings =
        queryClient.getQueryData<MapStringPrioritySchedulingEventPriorityScheduling>(
          queryKeyFactory.prioritySettings([...eventsIds, calendarRangeDifference, startTime])
        )
      const updatedEventsPrioritySettings = omit(previousEventsPrioritySettings, eventId)
      queryClient.setQueryData(
        queryKeyFactory.prioritySettings([...eventsIds, calendarRangeDifference, startTime]),
        updatedEventsPrioritySettings
      )

      return { previousEventsPrioritySettings, updatedEventsPrioritySettings }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeyFactory.prioritySettings([
          ...eventsIds,
          calendarRangeDifference,
          startTime,
        ]),
      })
    },
    onError: (_err, _updates, context) => {
      queryClient.setQueryData(
        queryKeyFactory.prioritySettings([...eventsIds, calendarRangeDifference, startTime]),
        context?.previousEventsPrioritySettings
      )
    },
  })
}

export const useCustomRules = () => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  return useQuery<CustomRules>({
    queryKey: ['custom-rules'],
    queryFn: () => {
      return userAvailability.getCustomPrioritySchedulingConfiguration(tenantId, userId)
    },
  })
}

export const useUpdateCustomRules = () => {
  const { userAvailability } = useApiClient()
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()

  return useMutation({
    mutationFn: (data: CustomRules) =>
      userAvailability.updateCustomPrioritySchedulingConfiguration(tenantId, userId, data),
    onMutate: updates => {
      const previousCustomRules = queryClient.getQueryData<CustomRules>(['custom-rules'])
      const updatedCustomRules = { ...previousCustomRules, ...updates }
      queryClient.setQueryData(['custom-rules'], updatedCustomRules)

      return { previousCustomRules, updatedCustomRules }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-rules'] })
    },
    onError: (_err, _updates, context) => {
      queryClient.setQueryData(['custom-rules'], context?.previousCustomRules)
    },
  })
}
